/**
 * Intel findings badge — shows the number of focal-point detections,
 * active surges, and completed investigations. Displayed as a small pill
 * in the header.
 *
 * Visual states:
 *   0 findings  → muted border, dim text ("INTEL 0")
 *   1-4         → amber border + bg, white text
 *   5+          → red border + bg, white text, pulse animation
 *   investigations > 0 → cyan border + bg ("INTEL" with count badge)
 *
 * WP-S13.2: Added investigation count tracking and cyan state.
 */

import { eventBus } from '@/core/events';
import { getAnalysisEngine } from '@/intelligence/analysis/engine';
import type { AnalysisResult } from '@/intelligence/analysis/types';

// Module-level cleanup for HMR safety
const activeUnsubs: (() => void)[] = [];

export function createIntelBadge(): HTMLElement {
  // Clean up previous subscriptions
  for (const unsub of activeUnsubs) unsub();
  activeUnsubs.length = 0;

  const pill = document.createElement('div');
  pill.className = 'intel-badge';
  pill.id = 'intel-badge';
  pill.style.cssText = `
    display: flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 3px; cursor: pointer;
    font-family: inherit;
  `;

  let lastAnalysisCount = -1;
  let investigationCount = 0;

  function render(result: AnalysisResult | null): void {
    const focalCount = result ? result.focalPoints.length : 0;
    const surgeCount = result ? result.surges.length : 0;
    const analysisTotal = focalCount + surgeCount;

    // Skip DOM update if nothing changed
    if (analysisTotal === lastAnalysisCount) return;
    lastAnalysisCount = analysisTotal;

    updatePill(analysisTotal);
  }

  function updatePill(_analysisTotal: number): void {
    pill.textContent = '';

    // INTEL label
    const label = document.createElement('span');
    label.style.cssText = 'font-size: 9px; font-weight: 600;';
    label.textContent = 'INTEL';
    pill.appendChild(label);

    // Count badge
    const countBadge = document.createElement('span');
    countBadge.style.cssText = `
      font-size: 9px; font-weight: 700; padding: 0 5px;
      border-radius: 2px; min-width: 18px; text-align: center;
    `;
    const totalDisplay = (lastAnalysisCount < 0 ? 0 : lastAnalysisCount) + investigationCount;
    countBadge.textContent = String(totalDisplay);
    pill.appendChild(countBadge);

    // Style based on state
    pill.classList.remove('intel-badge--active', 'intel-badge--critical', 'intel-badge--investigations');

    if (investigationCount > 0) {
      // Cyan state for investigations
      pill.style.background = '#00b4d822';
      pill.style.borderColor = '#00b4d8';
      pill.style.color = '#00b4d8';
      countBadge.style.background = '#00b4d8';
      countBadge.style.color = '#080b12';
      pill.classList.add('intel-badge--investigations');
    } else if (totalDisplay >= 5) {
      pill.classList.add('intel-badge--critical');
      pill.style.background = '';
      pill.style.borderColor = '';
      pill.style.color = '';
      countBadge.style.background = '';
      countBadge.style.color = '';
    } else if (totalDisplay >= 1) {
      pill.classList.add('intel-badge--active');
      pill.style.background = '';
      pill.style.borderColor = '';
      pill.style.color = '';
      countBadge.style.background = '';
      countBadge.style.color = '';
    } else {
      pill.style.background = 'transparent';
      pill.style.borderColor = 'var(--border-primary, #1a1f2e)';
      pill.style.color = 'var(--text-muted, #334155)';
      countBadge.style.background = 'var(--border-primary, #1a1f2e)';
      countBadge.style.color = 'var(--text-muted, #334155)';
    }

    // Tooltip
    const parts: string[] = [];
    if (lastAnalysisCount > 0) parts.push(`${lastAnalysisCount} analysis finding${lastAnalysisCount !== 1 ? 's' : ''}`);
    if (investigationCount > 0) parts.push(`${investigationCount} investigation${investigationCount !== 1 ? 's' : ''} complete`);
    pill.title = parts.length > 0 ? parts.join(', ') : 'No active intelligence findings';
  }

  // Initial state from analysis engine
  const engine = getAnalysisEngine();
  render(engine ? engine.getLastResult() : null);

  // Subscribe to analysis results
  activeUnsubs.push(eventBus.on('analysis:complete', (result) => {
    render(result);
  }));

  // Subscribe to investigation completions (WP-S13.2)
  activeUnsubs.push(eventBus.on('investigation:complete', () => {
    investigationCount++;
    lastAnalysisCount = -1; // Force re-render
    render(engine ? engine.getLastResult() : null);
  }));

  // Click handler: scroll to / activate AI Investigations tab (WP-S13.2)
  pill.addEventListener('click', () => {
    const invMount = document.getElementById('bottom-investigation-mount');
    if (invMount) {
      invMount.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  });

  // Attach destroy for teardown
  (pill as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const unsub of activeUnsubs) unsub();
    activeUnsubs.length = 0;
  };

  return pill;
}
